import React, { useCallback, useContext, useEffect, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { CommentBoxConfigProps, CommentBoxConfigValue } from './types';
import LibraryTitleInput from '../library-title-input';
import { NewInput } from '../../PIM/common/NewInput';
import FieldSetupPanelContext from './field-setup-panel.context';
import TextOptions from './text-options';
import type { TextOptionsPatch } from './text-options.types';

/**
 * CommentBoxConfig component for configuring comment box form controls in the system canvas.
 * Allows users to set up comment box properties and save configurations to library.
 * @param value - Current comment box configuration value
 * @param onChange - Callback function called when configuration changes
 * @param className - Optional CSS class name for styling
 * @param disabled - Whether the configuration controls should be disabled
 * @param showLibraryTitle - Whether to show the library title input section
 */
const CommentBoxConfig: React.FC<CommentBoxConfigProps> = ({
  value,
  onChange,
  className = '',
  disabled = false,
  showLibraryTitle = true,
  width,
  onWidthChange,
  widthError,
  hideTitle = false,
}) => {
  const { t } = useTranslation('custom-module-system-canvas');
  const titlePlaceholder = t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER');
  const showWidthRow = onWidthChange != null;
  const showOptionsPart = useContext(FieldSetupPanelContext) !== 'setup';

  const [localValue, setLocalValue] = useState<CommentBoxConfigValue>(value);
  const [libraryTitle, setLibraryTitle] = useState<string>('');

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const updateTitle = useCallback(
    (next: string) => {
      setLocalValue((prev) => {
        const nextVal = { ...prev, title: next };
        onChange(nextVal);
        return nextVal;
      });
    },
    [onChange],
  );

  const handleLibrarySave = useCallback(() => {
    setLibraryTitle('');
  }, []);

  const handleLibrarySelect = useCallback(
    (controlTypeData: any) => {
      const fetchedConfig = controlTypeData?.content?.content as CommentBoxConfigValue | undefined;
      if (fetchedConfig) {
        setLocalValue(fetchedConfig);
        onChange(fetchedConfig);
      }
    },
    [onChange],
  );

  /**
   * Applies a Character Limit or placeholder change from the Options tab.
   * @param patch - The changed option.
   */
  const updateTextOptions = (patch: TextOptionsPatch) => {
    const nextVal = { ...localValue, ...patch };
    setLocalValue(nextVal);
    onChange(nextVal);
  };

  return (
    <div className={`cm-maxw-520 ${className}`}>
      {showWidthRow && (
        <div className="mb-2">
          <label className="f-12 d-block">Width (%)</label>
          <NewInput
            type="text"
            placeholder="e.g. 25% or 150px"
            value={width ?? ''}
            onChange={(e) => onWidthChange(e.target.value.trim())}
            title="Field width when rendered (e.g. 25% or 150px)"
            invalid={!!widthError}
          />
          {widthError && <div className="invalid-feedback d-block">{widthError}</div>}
        </div>
      )}
      {showWidthRow && (
        <div className="mb-2">
          <p className="m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.COMMENT.TITLE')}</p>
          <NewInput
            type="text"
            placeholder={titlePlaceholder}
            value={localValue.title}
            onChange={(e) => updateTitle(e.target.value)}
            disabled={disabled}
          />
        </div>
      )}
      {showLibraryTitle && (
        <LibraryTitleInput
          value={libraryTitle}
          onChange={setLibraryTitle}
          onSave={handleLibrarySave}
          onSelect={handleLibrarySelect}
          controlType="comment"
          contentData={localValue}
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER_LIBRARY_TITLE')}
        />
      )}
      {!showWidthRow && !hideTitle && (
        <div className="mb-2">
          <p className="m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.COMMENT.TITLE')}</p>
          <NewInput
            type="text"
            placeholder={titlePlaceholder}
            value={localValue.title}
            onChange={(e) => updateTitle(e.target.value)}
            disabled={disabled}
          />
        </div>
      )}
      {showOptionsPart && (
        <TextOptions
          characterLimit={localValue.characterLimit ?? null}
          placeholder={localValue.placeholder ?? ''}
          placeholderLabel={t('CUSTOM_MODULE.SYSTEM_CANVAS.PLACEHOLDER_COMMENT')}
          multiline
          disabled={disabled}
          onChange={updateTextOptions}
        />
      )}
    </div>
  );
};

export default memo(CommentBoxConfig);
